import type { KeyboardEvent } from 'react'

import copyJson from '../../demo/fixtures/copy.json'
import enumsJson from '../../demo/fixtures/enums.json'
import { formatUsd } from '../../demo/format'
import { selectAutopilot, selectViolationsOf } from '../../demo/selectors'
import { demoProducts, marketLabel } from '../../demo/store'
import { useDemoStore } from '../../demo/store'
import type { Campaign, CampaignStatus, Objective } from '../../demo/types'
import { AutopilotBadge, Badge, Icon, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui'
import { ResultCell } from './ResultCell'
import { AUTOPILOT_COPY } from './autopilot'
import { COMPLIANCE_COPY, type MonitorTabKey } from './compliance'
import styles from './CampaignTable.module.css'

type BadgeTone = 'green' | 'red' | 'orange' | 'gray' | 'blue'

interface EnumFixture {
  objective: Record<Objective, string>
  status: Record<CampaignStatus, string>
  statusTone: Record<CampaignStatus, BadgeTone>
}

interface CampaignTableProps {
  campaigns: Campaign[]
  tab: MonitorTabKey
  runsByCampaign: ReadonlyMap<string, number>
  onCampaignClick: (id: string) => void
}

const enumFixture = enumsJson as unknown as EnumFixture
const productsById = new Map(demoProducts.map((product) => [product.id, product]))

export function CampaignTable({ campaigns, tab, runsByCampaign, onCampaignClick }: CampaignTableProps) {
  const ads = useDemoStore((state) => state.ads)
  const violations = useDemoStore((state) => state.violations)
  const openFromKeyboard = (event: KeyboardEvent<HTMLTableRowElement>, id: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onCampaignClick(id)
    }
  }

  return (
    <Table className={styles.table} data-scope={tab === 'autopilot' ? 'monitor-ap-table' : undefined}>
      <TableHead>
        <TableRow>
          <TableHeader>{copyJson.labels.colProduct}</TableHeader>
          <TableHeader>{copyJson.labels.colStatus}</TableHeader>
          <TableHeader>{copyJson.labels.colObjective}</TableHeader>
          <TableHeader>{copyJson.labels.colMarket}</TableHeader>
          <TableHeader>{copyJson.labels.colBudget}</TableHeader>
          <TableHeader>{copyJson.labels.colResult}</TableHeader>
          {tab === 'violation' ? <TableHeader className={styles.violationHeader}>{COMPLIANCE_COPY.violationColumn}</TableHeader> : null}
          {tab === 'autopilot' ? <TableHeader className={styles.autopilotColumn}>{AUTOPILOT_COPY.column}</TableHeader> : null}
          <TableHeader aria-label="进入详情" />
        </TableRow>
      </TableHead>
      <TableBody>
        {campaigns.map((campaign) => {
          const product = productsById.get(campaign.productId)
          const market = campaign.markets[0] ? marketLabel(campaign.markets[0]) : '—'
          const moreMarkets = campaign.markets.length - 1
          const autopilotOn = selectAutopilot(campaign.id)?.on === true
          return (
            <TableRow
              key={campaign.id}
              aria-label={`查看 ${campaign.adName}`}
              clickable
              tabIndex={0}
              onClick={() => onCampaignClick(campaign.id)}
              onKeyDown={(event) => openFromKeyboard(event, campaign.id)}
            >
              <TableCell>
                <div className={styles.product}>
                  <img className={styles.thumb} src={`/${product?.image ?? ''}`} alt={product?.name ?? campaign.adName} />
                  <div>
                    <p className={styles.productName}>
                      {product?.name ?? campaign.adName}
                      {autopilotOn ? (
                        <span className={styles.autopilotBadge} data-scope="monitor-ap-badge">
                          <AutopilotBadge size="sm" />
                        </span>
                      ) : null}
                    </p>
                    <p className={styles.adName}>{campaign.adName}</p>
                  </div>
                </div>
              </TableCell>
              <TableCell><Badge tone={enumFixture.statusTone[campaign.status]}>{enumFixture.status[campaign.status]}</Badge></TableCell>
              <TableCell>
                <span className={styles.objective}>
                  <Icon name={campaign.objective === 'sales' ? 'cart' : 'eye'} size={18} />
                  {enumFixture.objective[campaign.objective]}
                </span>
              </TableCell>
              <TableCell className={styles.market}>{market}{moreMarkets > 0 ? ` +${moreMarkets}` : ''}</TableCell>
              <TableCell numeric>{formatUsd(campaign.spendMonth)} / {formatUsd(campaign.budget.total)}</TableCell>
              <TableCell><ResultCell campaign={campaign} /></TableCell>
              {tab === 'violation' ? (
                <TableCell className={styles.violationCount}>
                  {selectViolationsOf(violations, campaign.id).length} 条 / 共 {ads.filter((ad) => ad.campaignId === campaign.id).length} 条
                </TableCell>
              ) : null}
              {tab === 'autopilot' ? (
                <TableCell className={styles.autopilotColumn} data-scope="monitor-ap-col" numeric>
                  {runsByCampaign.get(campaign.id) ?? 0} 次
                </TableCell>
              ) : null}
              <TableCell className={styles.chevronCell}><Icon className={styles.chevron} name="chevron-right" size={18} strokeWidth={2} /></TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
